/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { MouseEvent as ReactMouseEvent, useMemo } from 'react';
import { MovementEntry } from './TenderMovementsTable';
import { formatDate } from '~src/utils/formatDate';
import { TableProps, Tooltip } from 'antd';
import { EStyleObj } from '~src/types';
import { useIntl } from 'react-intl';
import IconButton from '~components/_common/IconButton/IconButton';
import { getReadableType } from '~src/utils/movementsUtils/movementsUtils.tsx';
import moment from 'moment';

type useMovementColumnsProps = {
  viewOnly?: boolean;
  onMenuDetailsClick?: (
    e: ReactMouseEvent<HTMLAnchorElement, MouseEvent>,
    record: MovementEntry
  ) => void;
  styles: EStyleObj;
  isStartFetch?: boolean;
};

const formatIfTimestamp = (val: string | number | null | undefined): string => {
  const num = Number(val);
  if (!isNaN(num) && num > 1_000_000_000 && num < 2_000_000_000) {
    return moment.unix(num).format('DD-MM-YYYY');
  }
  return val ?? '—';
};

const hasFirstReportDatetime = (val: any): boolean => {
  if (val === null || val === undefined) return false;
  const num = Number(val);
  return !Number.isNaN(num) && num > 0;
};

const MovementStatusIcons = ({
  record,
  intl,
}: {
  record: MovementEntry;
  intl: any;
}) => {
  const hasIgnore = record?.report_data?.is_ignore === true;
  const hasReportSent = hasFirstReportDatetime(
    record?.report_data?.first_report_datetime
  );

  const hasInCurrentLetter = record?.report_data?.is_in_current_report === true;

  // Public flag currently ignored
  // const hasPublic = record?.is_public === true;
  // const hasPrivate = record?.is_public === false;

  if (!hasIgnore && !hasReportSent && !hasInCurrentLetter) return null; // && !hasPublic && !hasPrivate

  const wrap = (node: any, id: string, defaultMessage: string) => (
    <Tooltip title={intl.formatMessage({ id, defaultMessage })}>
      {/* span fixes Tooltip hover on custom components */}
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        {node}
      </span>
    </Tooltip>
  );

  const iconWrapStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  };

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        width: '100%',
        flexWrap: 'nowrap',
      }}>
      {hasInCurrentLetter
        ? wrap(
            <span style={iconWrapStyle}>
              <IconButton
                iconStyle="far"
                icon="memo"
                color="blue"
                backgroundOff
              />
            </span>,
            'movement-flag-in-current-letter',
            'Included in current correction letter'
          )
        : null}

      {hasIgnore
        ? wrap(
            <span style={iconWrapStyle}>
              <IconButton
                iconStyle="fas"
                icon="ban"
                color="red"
                backgroundOff
              />
            </span>,
            'movement-flag-ignored',
            'Ignored'
          )
        : null}

      {hasReportSent
        ? wrap(
            <span style={iconWrapStyle}>
              <IconButton
                iconStyle="fas"
                icon="memo-circle-check"
                color="green"
                backgroundOff
              />
            </span>,
            'movement-flag-report-sent',
            'Already included in a sent report'
          )
        : null}

      {/* Public flag ignored for now */}
      {/* {hasPublic
        ? wrap(
            <span style={iconWrapStyle}>
              <IconButton iconStyle="fas" icon="globe" backgroundOff />
            </span>,
            'movement-flag-public',
            'Public'
          )
        : null}

      {hasPrivate
        ? wrap(
            <span style={iconWrapStyle}>
              <IconButton iconStyle="fas" icon="lock" backgroundOff />
            </span>,
            'movement-flag-private',
            'Private'
          )
        : null} */}
    </div>
  );
};

export const useMovementColumns = ({
  viewOnly,
  onMenuDetailsClick,
  styles,
  isStartFetch,
}: useMovementColumnsProps) => {
  const intl = useIntl();

  const allColumns = useMemo<TableProps<MovementEntry>['columns']>(
    () => [
      {
        title: '',
        width: 40,
        align: 'center',
        dataIndex: 'action',
        render: (type, record) => {
          return {
            props: {
              // style: getBackgroundRow(record),
            },
            children: (
              <a
                className="ant-dropdown-link"
                onClick={(e) =>
                  onMenuDetailsClick ? onMenuDetailsClick(e, record) : null
                }
                css={styles.dotAnchor}>
                <IconButton
                  iconStyle="fal"
                  icon="ellipsis-v"
                  customStyle={styles.dotIcon}
                  color={'darkGray'}
                  tooltip={intl.formatMessage({ id: 'actions' })}
                />
              </a>
            ),
          };
        },
      },
      {
        title: intl.formatMessage({
          id: 'movement-status',
          defaultMessage: 'Status',
        }),
        width: 90,
        align: 'center',
        dataIndex: 'status',
        render: (_val: any, record) => (
          <MovementStatusIcons record={record} intl={intl} />
        ),
      },
      {
        title: intl.formatMessage({ id: 'type' }),
        dataIndex: 'type',
        width: '200px',
        render: (val: number, record) => (
          <div>
            {getReadableType(val, intl)}
            {record.target_entities && record.target_entities.length > 0 && (
              <div
                style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {record.target_entities[0].type}:{' '}
                {record.target_entities[0].display_name}
              </div>
            )}
          </div>
        ),
      },
      {
        title: intl.formatMessage({ id: 'summary' }),
        dataIndex: 'summary',
        render: (val: { head?: string; actions?: string[] }, record) => (
          <div>
            <h2 style={{ fontSize: '14px', marginBottom: 4 }}>{val?.head}</h2>
            {val?.actions?.map((action, idx) => (
              <div key={idx} style={{ fontSize: '12px', color: '#666' }}>
                • {action}
              </div>
            ))}
            {record.changes && record.changes.length > 0 && (
              <div
                style={{
                  marginTop: 8,
                  borderTop: '1px dashed #eee',
                  paddingTop: 4,
                }}>
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: 'bold',
                    marginBottom: 2,
                  }}>
                  {intl.formatMessage({ id: 'movement-changes' })}
                </div>
                {record.changes.slice(0, 2).map((change, idx) => (
                  <div
                    key={`change-${idx}`}
                    style={{ fontSize: '11px', color: '#666' }}>
                    <span style={{ fontWeight: 'bold' }}>{change.field}:</span>{' '}
                    <span>{formatIfTimestamp(change.old_value)}</span> →{' '}
                    <span>{formatIfTimestamp(change.new_value)}</span>
                  </div>
                ))}

                {record.changes.length > 2 && (
                  <div
                    style={{
                      fontSize: '11px',
                      color: '#888',
                      fontStyle: 'italic',
                    }}>
                    {intl.formatMessage(
                      {
                        id: 'movement-more-changes',
                      },
                      { count: record.changes.length - 2 }
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ),
      },
      {
        title: intl.formatMessage({ id: 'movement-created' }),
        dataIndex: 'created_at',
        width: '300px',
        render: (val: number, record) => (
          <div>
            <div>{formatDate(val, 'short')}</div>
            <div>{record.created_by_user_name}</div>
          </div>
        ),
      },
    ],
    [isStartFetch, onMenuDetailsClick, intl]
  );

  const printColumns = useMemo<TableProps<MovementEntry>['columns']>(
    () => [
      {
        title: intl.formatMessage({ id: 'type' }),
        dataIndex: 'type',
        width: '200px',
        render: (val: number, record) => (
          <div>
            {getReadableType(val)}
            {record.target_entities && record.target_entities.length > 0 && (
              <div
                style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {record.target_entities[0].type}:{' '}
                {record.target_entities[0].display_name}
              </div>
            )}
          </div>
        ),
        // sorter: viewOnly ? false : (a, b) => a.action.localeCompare(b.action),
      },
      {
        title: intl.formatMessage({ id: 'movement-summary' }),
        dataIndex: 'summary',
        render: (val: { head?: string; actions?: string[] }, record) => (
          <div>{intl.formatMessage({ id: 'movement-full-change-list' })}</div>
        ),
      },
      {
        title: intl.formatMessage({ id: 'movement-created' }),
        dataIndex: 'created_at',
        width: '200px',
        render: (val: number, record) => (
          <div>
            <div>{formatDate(val, 'short')}</div>
            <div>{record.created_by_user_name}</div>
          </div>
        ),
      },
    ],
    [isStartFetch, intl]
  );

  return isStartFetch ? printColumns : allColumns;
};
